// Customer-facing pricing — single source of truth.
//
// Mirrors docs PRICING-customer-facing.md (last agreed June 2026). Pricing
// answers TWO questions, kept separate on every surface:
//   1. How hands-on do you want to be?  → the involvement tier (headline).
//   2. (Done-for-you only) Own it or have us host it? → own-code vs hosted.
//
// Edit numbers HERE, not in the components.

// Booking: ONE Calendly account for everything. The paid Same-Day Launch
// event lives on the SDL page; this is the free qualification call every
// "talk to a human first" CTA points at.
// Free calls use the site's own Google Calendar picker at /book. The single
// Calendly event (free plan = one event type) is reserved for paid Same-Day Launch.
export const FREE_CALL_URL = '/book';

// 1 — Involvement tiers (the headline ladder). Blueprint + DIY are free on
// purpose (Helios runs on local hardware; revenue is affiliate + upsells) —
// never show a price on them. The ladder must read fast/cheap → custom/slow:
// nothing here may undercut Same-Day Launch at $497 (websites under $1,500
// ARE Same-Day Launch), and paid tiers route to the free call, not /intake.
export const INVOLVEMENT_TIERS = [
  {
    key: 'blueprint',
    name: 'Blueprint',
    price: 'Free',
    tagline: 'A custom plan from Helios',
    description:
      'Talk to Helios and get a custom plan — what to build, the stack, the steps, and the cost. No signup.',
    features: ['Custom build plan', 'Recommended stack', 'Step-by-step roadmap', 'Ballpark cost'],
    cta: { label: 'Start free with Helios', to: '/intake' },
  },
  {
    key: 'diy',
    name: 'DIY',
    price: 'Free',
    tagline: 'Build it yourself',
    description:
      'Your blueprint plus templates, resource guides, and ongoing Helios access. You build it with our recommended tools.',
    features: ['Everything in Blueprint', 'Templates & resource guides', 'Ongoing Helios access', 'Recommended-tool setup'],
    cta: { label: 'Get your blueprint', to: '/intake' },
    note: 'Free — we earn from the tools we recommend, never from you.',
  },
  {
    key: 'guided',
    name: 'Setup Session',
    price: '$350 flat',
    tagline: 'We set it up together',
    description:
      'A 90-minute working session for things that are not a website — a tool, a workflow, a migration. Custom instructions prepped in advance, everything stood up in your own accounts.',
    features: ['90-minute working session', 'Custom setup instructions', 'Built in your own accounts', 'You own it + learn how it works'],
    cta: { label: 'Book a free call first', href: FREE_CALL_URL },
  },
  {
    key: 'dfy',
    name: 'Done-for-you',
    price: 'From $1,500',
    tagline: 'We build it, you own it',
    description:
      'A fully custom build — AI-accelerated — with ownership handed over. Need a straightforward site for less? That is Same-Day Launch, $497, live today.',
    features: ['We build it end-to-end', 'Ownership handed to you', 'Own-the-code or hosted-with-us', 'Optional ongoing care'],
    cta: { label: 'Book a free call', href: FREE_CALL_URL },
  },
];

// 2 — Done-for-you build anchors ("from" prices by project type).
export const BUILD_ANCHORS = [
  { type: 'Website / small-business / brochure site', from: 1500, note: 'Simple site? Same-Day Launch is $497, live today' },
  { type: 'AI prototype → production', from: 2000, note: 'Harden a Lovable / Bolt / v0 / Cursor build' },
  { type: 'Web app / marketplace', from: 5000, note: 'Stripe Connect, auth, payouts' },
  { type: 'Mobile app (iOS + Android)', from: 5000 },
  { type: 'Community app', from: 5000, note: 'Feeds, profiles, matching, moderation' },
];

// 3 — Care plans (the "hosted-with-us" monthly tiers).
export const CARE_PLANS = [
  { name: 'Basic', monthly: 39, includes: 'Hosting, monitoring, uptime, small fixes' },
  { name: 'Plus', monthly: 99, includes: '+ 2 hrs revisions/mo, priority support' },
  { name: 'Pro', monthly: 150, includes: '+ more revision hours, faster turnaround' },
];

// 4 — Add-ons.
export const ADD_ONS = [
  { name: 'Reality-Check Session', price: '$150', description: 'A gut-check call on an idea before any build.' },
  { name: 'Ownership transfer', price: 'Free, always', description: 'We hand over the code. Deploy anywhere — no lock-in.' },
];

// Disclaimer to keep wherever pricing shows (FTC + estimate framing).
export const PRICING_DISCLAIMER =
  'Automated estimates are confirmed by Josh before any commitment. Affiliate links (domains, hosting, email) carry a small commission and are disclosed.';

export const fmtUsd = (n) => `$${Number(n).toLocaleString()}`;
